import React, { useState, useEffect, useCallback } from "react";
import { diffWordsWithSpace, diffJson, diffLines, diffChars } from "diff";
import "prismjs/themes/prism-tomorrow.css";
import beautify from "json-beautify";
import ReactJson from "react-json-view";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faExchangeAlt,
  faFileUpload,
  faCopy,
  faDownload,
  faSync,
  faEye,
  faCode,
  faFileAlt,
  faChartBar,
  faMagic,
  faHistory,
} from "@fortawesome/free-solid-svg-icons";
import SEOHead from "../common/SEOHead";
import Reveal from "../components/Reveal";
import { trackEvent } from "../components/trackingUtils";
import { baseUrl } from "../config";
import "./TextComparison.css";

const TextComparison = () => {
  const [text1, setText1] = useState("");
  const [text2, setText2] = useState("");
  const [diffHtml, setDiffHtml] = useState("");
  const [formattedJson1, setFormattedJson1] = useState(null);
  const [formattedJson2, setFormattedJson2] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isJsonComparison, setIsJsonComparison] = useState(false);
  const [comparisonMode, setComparisonMode] = useState("words"); // words, lines, chars
  const [showStats, setShowStats] = useState(true);
  const [comparisonStats, setComparisonStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [showSideBySide, setShowSideBySide] = useState(true);
  const [dragOver, setDragOver] = useState({ left: false, right: false });

  useEffect(() => {
    trackEvent("PageLoad", "", "TextComparison");
  }, []);

  const formatJson = (jsonString) => {
    try {
      const jsonObject = JSON.parse(jsonString);
      return beautify(jsonObject, null, 2, 80);
    } catch (e) {
      return null;
    }
  };

  const calculateStats = (text1, text2, diff) => {
    const stats = {
      text1Length: text1.length,
      text2Length: text2.length,
      text1Words: text1.split(/\s+/).filter((w) => w).length,
      text2Words: text2.split(/\s+/).filter((w) => w).length,
      additions: diff.filter((part) => part.added).length,
      deletions: diff.filter((part) => part.removed).length,
      unchanged: diff.filter((part) => !part.added && !part.removed).length,
      similarity: 0,
    };

    // Calculate similarity percentage
    const totalParts = stats.additions + stats.deletions + stats.unchanged;
    stats.similarity =
      totalParts > 0 ? Math.round((stats.unchanged / totalParts) * 100) : 0;

    return stats;
  };

  const compareTexts = useCallback(() => {
    if (!text1.trim() || !text2.trim()) {
      setError("Please enter text in both fields to compare.");
      return;
    }

    setLoading(true);
    setError("");
    setDiffHtml("");
    setFormattedJson1(null);
    setFormattedJson2(null);
    setComparisonStats(null);

    try {
      trackEvent(
        "TextComparison",
        "Compare",
        isJsonComparison ? "JSON" : comparisonMode
      );

      if (isJsonComparison) {
        // Format JSON
        const formatted1 = formatJson(text1);
        const formatted2 = formatJson(text2);

        if (formatted1 === null || formatted2 === null) {
          setError(
            "Invalid JSON format. Please check your input and ensure it's valid JSON."
          );
          setLoading(false);
          return;
        }

        // Parse JSON for comparison
        const obj1 = JSON.parse(formatted1);
        const obj2 = JSON.parse(formatted2);

        // Compare JSON
        const diff = diffJson(obj1, obj2);
        const stats = calculateStats(formatted1, formatted2, diff);

        // Generate HTML for JSON differences
        const html = diff
          .map((part) => {
            if (part.added)
              return `<span class="json-added">${part.value}</span>`;
            if (part.removed)
              return `<span class="json-removed">${part.value}</span>`;
            return part.value;
          })
          .join("");

        setDiffHtml(html);
        setFormattedJson1(obj1);
        setFormattedJson2(obj2);
        setComparisonStats(stats);
      } else {
        // Choose comparison function based on mode
        let diff;
        switch (comparisonMode) {
          case "lines":
            diff = diffLines(text1, text2);
            break;
          case "chars":
            diff = diffChars(text1, text2);
            break;
          case "words":
          default:
            diff = diffWordsWithSpace(text1, text2);
            break;
        }

        const stats = calculateStats(text1, text2, diff);

        // Generate HTML for text differences
        const html = diff
          .map((part) => {
            if (part.added) {
              return `<span class="added">${part.value}</span>`;
            }
            if (part.removed) {
              return `<span class="removed">${part.value}</span>`;
            }
            return `<span class="unchanged">${part.value}</span>`;
          })
          .join("");

        setDiffHtml(html);
        setComparisonStats(stats);
      }

      // Add to history
      const historyEntry = {
        id: Date.now(),
        timestamp: new Date(),
        type: isJsonComparison ? "JSON" : comparisonMode,
        text1: text1.substring(0, 100) + (text1.length > 100 ? "..." : ""),
        text2: text2.substring(0, 100) + (text2.length > 100 ? "..." : ""),
      };
      setHistory((prev) => [historyEntry, ...prev.slice(0, 9)]); // Keep last 10
    } catch (err) {
      console.error("Comparison error:", err);
      setError("An error occurred while comparing texts. Please try again.");
      trackEvent("TextComparison", "Error", err.message);
    } finally {
      setLoading(false);
    }
  }, [text1, text2, isJsonComparison, comparisonMode]);

  const clearText = () => {
    trackEvent("TextComparison", "Clear", "User_Action");
    setText1("");
    setText2("");
    setDiffHtml("");
    setFormattedJson1(null);
    setFormattedJson2(null);
    setError("");
    setComparisonStats(null);
  };

  const swapTexts = () => {
    trackEvent("TextComparison", "Swap", "User_Action");
    const temp = text1;
    setText1(text2);
    setText2(temp);
  };

  const copyToClipboard = (text, type) => {
    navigator.clipboard.writeText(text).then(() => {
      trackEvent("TextComparison", "Copy", type);
      // Could add a toast notification here
    });
  };

  const downloadResult = () => {
    const content = diffHtml.replace(/<[^>]*>/g, ""); // Strip HTML tags
    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "comparison-result.txt";
    a.click();
    URL.revokeObjectURL(url);
    trackEvent("TextComparison", "Download", "Result");
  };

  const handleDrop = (e, side) => {
    e.preventDefault();
    setDragOver({ left: false, right: false });

    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      handleFileUpload(
        { target: { files } },
        side === "left" ? setText1 : setText2
      );
    }
  };

  const handleDragOver = (e, side) => {
    e.preventDefault();
    setDragOver((prev) => ({ ...prev, [side]: true }));
  };

  const handleDragLeave = (e, side) => {
    e.preventDefault();
    setDragOver((prev) => ({ ...prev, [side]: false }));
  };

  const handleFileUpload = (event, setText) => {
    const file = event.target.files[0];
    if (file) {
      if (file.size > 10 * 1024 * 1024) {
        // 10MB limit
        setError(
          "File size too large. Please choose a file smaller than 10MB."
        );
        return;
      }

      const reader = new FileReader();
      reader.onload = (e) => {
        setText(e.target.result);
        trackEvent("TextComparison", "File_Upload", file.type);
      };
      reader.onerror = () => {
        setError("Error reading file. Please try again.");
      };
      reader.readAsText(file);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEOHead
        title="Professional Text & JSON Comparison Tool | GeniusNexa"
        description="Advanced text and JSON comparison tool with side-by-side diff, syntax highlighting, and detailed analysis. Perfect for developers, content writers, and data analysts."
        keywords="text comparison, diff tool, JSON comparison, code diff, file comparison, text analyzer, developer tools, GeniusNexa"
        url={`${baseUrl}/tools/text-comparison`}
      />
      {/* Hero Section */}
      <Reveal>
        <section className="relative bg-gradient-to-r from-[#0b1220] via-[#1a2332] to-[#0b1220] text-white py-20 overflow-hidden">
          <div className="absolute inset-0">
            <div className="absolute w-96 h-96 bg-[#E5C558]/10 rounded-full blur-3xl top-0 right-0 animate-pulse"></div>
            <div
              className="absolute w-96 h-96 bg-blue-500/10 rounded-full blur-3xl bottom-0 left-0 animate-pulse"
              style={{ animationDelay: "1s" }}
            ></div>
          </div>

          <div className="relative z-10 px-4 text-center max-w-6xl mx-auto">
            <div className="inline-flex items-center justify-center w-20 h-20 mb-6 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] rounded-full shadow-lg">
              <FontAwesomeIcon
                icon={faCode}
                className="text-2xl text-[#0b1220]"
              />
            </div>
            <h1 className="text-5xl md:text-6xl font-bold mb-6">
              <span className="bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Advanced{" "}
              </span>
              <span className="bg-gradient-to-r from-[#E5C558] to-[#f5d56d] bg-clip-text text-transparent">
                Text Comparison
              </span>
            </h1>
            <p className="text-xl md:text-2xl mb-8 text-gray-200 max-w-3xl mx-auto">
              Compare texts, JSON objects, and code with intelligent diff
              algorithms, syntax highlighting, and comprehensive analysis
            </p>
            <div className="flex flex-wrap justify-center gap-4 text-sm text-gray-300">
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faMagic} />
                Multiple Comparison Modes
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faEye} />
                Side-by-Side View
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faChartBar} />
                Detailed Statistics
              </span>
              <span className="flex items-center gap-2">
                <FontAwesomeIcon icon={faFileAlt} />
                File Upload Support
              </span>
            </div>
          </div>
        </section>
      </Reveal>

      {/* Main Content Section - Light */}
      <div className="container mx-auto px-4 py-16">
        {/* Main Tool Section */}
        <section>
          <div className="max-w-7xl mx-auto">
            {/* Tool Controls */}
            <Reveal>
              <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-8 mb-8 border border-gray-200 shadow-xl">
                <div className="flex flex-wrap items-center justify-between gap-6 mb-6">
                  <div className="flex items-center gap-4">
                    <h2 className="text-2xl font-bold text-gray-800">
                      Comparison Settings
                    </h2>
                    {comparisonStats && (
                      <div className="text-sm text-[#E5C558] bg-[#E5C558]/20 px-3 py-1 rounded-full border border-[#E5C558]/30">
                        {comparisonStats.similarity}% similar
                      </div>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-3">
                    <button
                      onClick={swapTexts}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-lg hover:shadow-[#E5C558]/30 text-[#0b1220] font-semibold rounded-xl transition-all duration-300 transform hover:scale-105"
                      disabled={!text1 || !text2}
                    >
                      <FontAwesomeIcon icon={faExchangeAlt} />
                      Swap
                    </button>
                    <button
                      onClick={clearText}
                      className="flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105"
                    >
                      <FontAwesomeIcon icon={faSync} />
                      Clear
                    </button>
                    {diffHtml && (
                      <button
                        onClick={downloadResult}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white rounded-xl transition-all duration-300 transform hover:scale-105"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Download
                      </button>
                    )}
                  </div>
                </div>

                {/* Mode Selection */}
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Comparison Type
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="json-mode"
                        checked={isJsonComparison}
                        onChange={(e) => setIsJsonComparison(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="json-mode" className="text-gray-700">
                        JSON Mode
                      </label>
                    </div>
                  </div>

                  {!isJsonComparison && (
                    <div className="space-y-2">
                      <label className="block text-sm font-medium text-gray-700">
                        Comparison Mode
                      </label>
                      <select
                        value={comparisonMode}
                        onChange={(e) => setComparisonMode(e.target.value)}
                        className="w-full px-3 py-2 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent"
                      >
                        <option value="words">Word-by-word</option>
                        <option value="lines">Line-by-line</option>
                        <option value="chars">Character-by-character</option>
                      </select>
                    </div>
                  )}

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      View Mode
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="side-by-side"
                        checked={showSideBySide}
                        onChange={(e) => setShowSideBySide(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="side-by-side" className="text-gray-700">
                        Side-by-side
                      </label>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Options
                    </label>
                    <div className="flex items-center gap-2">
                      <input
                        type="checkbox"
                        id="show-stats"
                        checked={showStats}
                        onChange={(e) => setShowStats(e.target.checked)}
                        className="w-4 h-4 text-purple-600 rounded focus:ring-purple-500"
                      />
                      <label htmlFor="show-stats" className="text-gray-700">
                        Show Statistics
                      </label>
                    </div>
                  </div>
                </div>

                {error && (
                  <div className="mb-6 p-4 bg-red-100 border border-red-300 rounded-xl text-red-700">
                    <FontAwesomeIcon icon={faExchangeAlt} className="mr-2" />
                    {error}
                  </div>
                )}
              </div>
            </Reveal>

            {/* Text Input Areas */}
            <Reveal>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
                {/* Left Text Input */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-200 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        className="text-[#E5C558]"
                      />
                      Text 1
                    </h3>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-lg text-[#0b1220] font-semibold rounded-lg cursor-pointer transition-all duration-300">
                        <FontAwesomeIcon icon={faFileUpload} />
                        Upload
                        <input
                          type="file"
                          accept=".txt,.json,.js,.py,.html,.css,.xml"
                          onChange={(e) => handleFileUpload(e, setText1)}
                          className="hidden"
                        />
                      </label>
                      <button
                        onClick={() => copyToClipboard(text1, "Text1")}
                        disabled={!text1}
                        className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-[#E5C558] hover:text-[#0b1220] text-white rounded-lg disabled:opacity-50 transition-all duration-300"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  </div>
                  <div
                    className={`relative ${
                      dragOver.left
                        ? "ring-2 ring-purple-400 bg-purple-500/20"
                        : ""
                    }`}
                    onDrop={(e) => handleDrop(e, "left")}
                    onDragOver={(e) => handleDragOver(e, "left")}
                    onDragLeave={(e) => handleDragLeave(e, "left")}
                  >
                    <textarea
                      value={text1}
                      onChange={(e) => setText1(e.target.value)}
                      rows={20}
                      placeholder="Enter your first text, JSON, or code here... You can also drag and drop files."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent resize-none font-mono text-sm leading-relaxed"
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                      {text1.length} characters
                    </div>
                  </div>
                </div>

                {/* Right Text Input */}
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-200 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faFileAlt}
                        className="text-[#E5C558]"
                      />
                      Text 2
                    </h3>
                    <div className="flex items-center gap-2">
                      <label className="flex items-center gap-2 px-3 py-1 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-lg text-[#0b1220] font-semibold rounded-lg cursor-pointer transition-all duration-300">
                        <FontAwesomeIcon icon={faFileUpload} />
                        Upload
                        <input
                          type="file"
                          accept=".txt,.json,.js,.py,.html,.css,.xml"
                          onChange={(e) => handleFileUpload(e, setText2)}
                          className="hidden"
                        />
                      </label>
                      <button
                        onClick={() => copyToClipboard(text2, "Text2")}
                        disabled={!text2}
                        className="flex items-center gap-1 px-2 py-1 bg-white/10 hover:bg-[#E5C558] hover:text-[#0b1220] text-white rounded-lg disabled:opacity-50 transition-all duration-300"
                      >
                        <FontAwesomeIcon icon={faCopy} />
                      </button>
                    </div>
                  </div>
                  <div
                    className={`relative ${
                      dragOver.right
                        ? "ring-2 ring-blue-400 bg-blue-500/20"
                        : ""
                    }`}
                    onDrop={(e) => handleDrop(e, "right")}
                    onDragOver={(e) => handleDragOver(e, "right")}
                    onDragLeave={(e) => handleDragLeave(e, "right")}
                  >
                    <textarea
                      value={text2}
                      onChange={(e) => setText2(e.target.value)}
                      rows={20}
                      placeholder="Enter your second text, JSON, or code here... You can also drag and drop files."
                      className="w-full px-4 py-3 bg-gray-50 border border-gray-300 rounded-xl text-gray-800 placeholder-gray-500 focus:ring-2 focus:ring-[#E5C558] focus:border-transparent resize-none font-mono text-sm leading-relaxed"
                    />
                    <div className="absolute bottom-2 right-2 text-xs text-gray-500">
                      {text2.length} characters
                    </div>
                  </div>
                </div>
              </div>
            </Reveal>

            {/* Compare Button */}
            <Reveal>
              <div className="text-center mb-8">
                <button
                  onClick={compareTexts}
                  disabled={loading || !text1.trim() || !text2.trim()}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-xl hover:shadow-[#E5C558]/30 text-[#0b1220] font-bold rounded-2xl transform hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none"
                >
                  {loading ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                      Analyzing...
                    </>
                  ) : (
                    <>
                      <FontAwesomeIcon icon={faMagic} />
                      Compare {isJsonComparison ? "JSON" : "Text"}
                    </>
                  )}
                </button>
              </div>
            </Reveal>

            {/* Statistics Panel */}
            {comparisonStats && showStats && (
              <Reveal>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 mb-8 border border-gray-200 shadow-xl">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faChartBar}
                      className="text-[#E5C558]"
                    />
                    Comparison Statistics
                  </h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#E5C558]">
                        {comparisonStats.similarity}%
                      </div>
                      <div className="text-sm text-gray-600">Similarity</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-green-400">
                        {comparisonStats.additions}
                      </div>
                      <div className="text-sm text-gray-600">Additions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-red-400">
                        {comparisonStats.deletions}
                      </div>
                      <div className="text-sm text-gray-300">Deletions</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-blue-400">
                        {comparisonStats.unchanged}
                      </div>
                      <div className="text-sm text-gray-300">Unchanged</div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#d4b54d]">
                        {comparisonStats.text1Words}
                      </div>
                      <div className="text-sm text-gray-300">
                        Words (Text 1)
                      </div>
                    </div>
                    <div className="text-center">
                      <div className="text-2xl font-bold text-[#f5d56d]">
                        {comparisonStats.text2Words}
                      </div>
                      <div className="text-sm text-gray-300">
                        Words (Text 2)
                      </div>
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Comparison Results */}
            {diffHtml && (
              <Reveal>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 mb-8 border border-gray-200 shadow-xl">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-xl font-semibold text-gray-800 flex items-center gap-2">
                      <FontAwesomeIcon
                        icon={faEye}
                        className="text-[#E5C558]"
                      />
                      Comparison Results
                    </h3>
                    <button
                      onClick={() => copyToClipboard(diffHtml, "Result")}
                      className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-[#E5C558] to-[#d4b54d] hover:shadow-lg text-[#0b1220] font-semibold rounded-xl transition-all duration-300"
                    >
                      <FontAwesomeIcon icon={faCopy} />
                      Copy Result
                    </button>
                  </div>
                  <div className="bg-gray-50 rounded-xl p-4 font-mono text-sm leading-relaxed overflow-auto max-h-96 border border-gray-200">
                    <div dangerouslySetInnerHTML={{ __html: diffHtml }} />
                  </div>
                </div>
              </Reveal>
            )}

            {/* JSON Tree View */}
            {formattedJson1 && formattedJson2 && (
              <Reveal>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 mb-8 border border-gray-200 shadow-xl">
                  <h3 className="text-xl font-semibold text-gray-800 mb-6 flex items-center gap-2">
                    <FontAwesomeIcon icon={faCode} className="text-[#E5C558]" />
                    JSON Tree View
                  </h3>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h4 className="text-lg font-medium text-[#E5C558] mb-3">
                        JSON Object 1
                      </h4>
                      <ReactJson
                        src={formattedJson1}
                        theme="tomorrow"
                        displayDataTypes={false}
                        displayObjectSize={false}
                        name={false}
                        collapsed={2}
                      />
                    </div>
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-200">
                      <h4 className="text-lg font-medium text-[#E5C558] mb-3">
                        JSON Object 2
                      </h4>
                      <ReactJson
                        src={formattedJson2}
                        theme="tomorrow"
                        displayDataTypes={false}
                        displayObjectSize={false}
                        name={false}
                        collapsed={2}
                      />
                    </div>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Comparison History */}
            {history.length > 0 && (
              <Reveal>
                <div className="bg-white/80 backdrop-blur-sm rounded-3xl p-6 border border-gray-200 shadow-xl">
                  <h3 className="text-xl font-semibold text-gray-800 mb-4 flex items-center gap-2">
                    <FontAwesomeIcon
                      icon={faHistory}
                      className="text-[#E5C558]"
                    />
                    Recent Comparisons
                  </h3>
                  <div className="space-y-3">
                    {history.slice(0, 5).map((entry) => (
                      <div
                        key={entry.id}
                        className="flex items-center justify-between p-3 bg-gray-50 rounded-xl border border-gray-200"
                      >
                        <div>
                          <div className="text-gray-800 font-medium">
                            {entry.type} Comparison
                          </div>
                          <div className="text-gray-600 text-sm">
                            {entry.timestamp.toLocaleString()}
                          </div>
                        </div>
                        <div className="text-sm text-gray-600 max-w-xs truncate">
                          {entry.text1} ↔ {entry.text2}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </Reveal>
            )}
          </div>
        </section>
      </div>
    </div>
  );
};

export default TextComparison;
